import {loginActions, loginSelectors} from 'features/login/redux';
import {isEmpty} from 'lodash';
import {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {IDiplomatObjectAceType} from 'types/diplomat/settings/permissions/DiplomatObjectAceType';
import {DiplomatObjectTypeUnion} from 'types/DiplomatObjectType';

export const useIsPermitted = (objectType: DiplomatObjectTypeUnion, id?: string) => {
   const itemsPermissions = useSelector(loginSelectors.itemsPermissions);
   const dispatch = useDispatch();
   const key = `${objectType}/${id}`;

   const perms = itemsPermissions[key];

   useEffect(() => {
      if (!perms && objectType && id) dispatch(loginActions.fetchItemsPermissions(key));
   }, [perms, dispatch, objectType, id]);

   const isPermitted = useCallback(
      (permission: IDiplomatObjectAceType, defaultValue: boolean = false) => {
         return perms && !isEmpty(perms)
            ? Boolean(perms?.aces?.some((ace) => permission === ace))
            : defaultValue;
      },
      [perms, key]
   );

   return isPermitted;
};

export const useIsPermittedOption = (
   permission: IDiplomatObjectAceType,
   objectType: DiplomatObjectTypeUnion,
   id?: string
) => {
   const isPermittedCb = useIsPermitted(objectType, id);

   const isPermitted = useMemo(() => isPermittedCb(permission), [isPermittedCb, permission]);

   return isPermitted;
};

export const useIsPermittedOptionDeffered = (
   permission: IDiplomatObjectAceType,
   objectType: DiplomatObjectTypeUnion,
   id?: string,
   defaultValue?: boolean
) => {
   const itemsPermissions = useSelector(loginSelectors.itemsPermissions);
   const dispatch = useDispatch();
   const key = `${objectType}/${id}`;

   const perms = itemsPermissions[key];

   const fetch = useCallback(() => {
      if (!perms && id) dispatch(loginActions.fetchItemsPermissions(key));
   }, [perms, dispatch, id]);

   const isPermitted = useMemo(() => {
      return perms ? Boolean(perms?.aces?.some((ace) => permission === ace)) : defaultValue;
   }, [perms, key, permission, defaultValue]);

   return [isPermitted, fetch] as const;
};
